import type { UIMessage } from "ai"
import type { ReactNode } from "react"
import { GraduationCap } from "lucide-react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { CoachMarkdown } from "@/components/coach-markdown"
import { splitLeadingQuote } from "@/lib/quote"
import { useSessionContext } from "@/lib/session"
import { useCoachFor } from "@/lib/use-coach"
import { cn } from "@/lib/utils"
import { COACH_FALLBACK_LABEL } from "@nl/shared"

export function partsToText(m: UIMessage): string {
  return m.parts
    .filter((p) => p.type === "text")
    .map((p) => (p as { text: string }).text)
    .join("")
}

export function CoachBubble({
  role,
  text,
  typing,
  streaming,
  coachStyleId,
  tools,
  attachments,
}: {
  role: "user" | "assistant"
  text: string
  typing?: boolean
  streaming?: boolean
  /** The coach of this conversation. Without it the currently picked one is
   *  shown, which is only right for a chat that has none of its own. */
  coachStyleId?: string
  /** What the coach looked up for this answer, shown above its text. */
  tools?: ReactNode
  /** What the coach hands over with the answer, shown under the bubble. */
  attachments?: ReactNode
}) {
  const { user } = useSessionContext()
  const coach = useCoachFor(coachStyleId)
  const userInitials = user?.initials ?? ""

  const isUser = role === "user"
  // Parsed from raw content, so a live message and one replayed from the
  // persisted history render the attached feedback quote identically.
  const quoted = isUser ? splitLeadingQuote(text) : null
  return (
    <div
      className={cn(
        "reveal flex gap-3",
        isUser ? "justify-end" : "justify-start"
      )}
    >
      {!isUser && (
        <Avatar className="h-8 w-8 flex-shrink-0 border border-[var(--rule)]">
          {coach?.portraitUrl ? (
            <AvatarImage src={coach.portraitUrl} alt="" loading="lazy" />
          ) : null}
          <AvatarFallback className="bg-accent text-primary">
            <GraduationCap className="h-4 w-4" />
          </AvatarFallback>
        </Avatar>
      )}
      <div
        className={cn(
          "max-w-[80%] space-y-1.5",
          isUser && "items-end text-right"
        )}
      >
        {/* Only the coach is labelled: an own message is right-aligned and
            colour-coded, so naming it adds nothing.
            The role travels with the name because this label is the one place
            it appears entirely without context — in a screenshot of a single
            bubble, "Norbert" alone would read like a colleague. */}
        {!isUser && (
          <div className="eyebrow">
            {coach
              ? `${coach.name} · ${COACH_FALLBACK_LABEL}`
              : COACH_FALLBACK_LABEL}
          </div>
        )}
        {/* Eine Karte ohne Text braucht keine leere Sprechblase.
            Der Prüfungscoach darf eine Frage wortlos stellen — die Karte sagt
            alles —, und der leere gerahmte Kasten darüber sah kaputt aus. */}
        {!(!isUser && !text && !tools && !typing && attachments) && (
          <div
            className={cn(
              "px-4 py-3 text-[14px] leading-[1.55]",
              // The advisor's own text stays literal; only the coach's replies are
              // markdown (the model is prompted to use light formatting).
              isUser
                ? "rounded-[10px] rounded-br-[2px] bg-primary whitespace-pre-wrap text-primary-foreground"
                : "rounded-[10px] rounded-bl-[2px] border border-[var(--rule)] bg-card",
              typing && "animate-pulse text-muted-foreground",
              streaming && "streaming-caret"
            )}
          >
            {isUser ? (
              quoted ? (
                <>
                  <div className="mb-2 border-l-2 border-primary-foreground/40 pl-2.5 text-left text-[13px] whitespace-pre-wrap text-primary-foreground/85 italic">
                    {quoted.quote}
                  </div>
                  {quoted.question}
                </>
              ) : (
                text
              )
            ) : (
              <>
                {tools}
                {text ? (
                  <CoachMarkdown text={text} />
                ) : typing && !tools ? (
                  "…"
                ) : null}
              </>
            )}
          </div>
        )}
        {attachments}
      </div>
      {isUser && (
        <Avatar className="h-8 w-8 flex-shrink-0 border border-[var(--rule)]">
          <AvatarFallback className="bg-accent font-mono text-[11px] tracking-wider text-accent-foreground uppercase">
            {userInitials}
          </AvatarFallback>
        </Avatar>
      )}
    </div>
  )
}
